import { useState, useEffect, useCallback, useRef } from "react";
import { ProcuraconContextType } from "newui/types/ContextType.type";
import {
  createConversation,
  fetchConversationsByContextId,
} from "newui/features/Conversation/api";
import type { Conversation } from "newui/features/Conversation/types/Conversation.type";

interface UseClarificationChatReturn {
  conversation: Conversation | null;
  error: string | null;
  isLoading: boolean;
  onConversationUpdated: (conversation: Conversation) => void;
  startChat: (firstMessage: string) => Promise<void>;
}

export const useClarificationChat = (
  clarificationId: number,
  rfxId: number,
  isOpen: boolean
): UseClarificationChatReturn => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedForRef = useRef<number | null>(null);

  const fetchExistingConversation = useCallback(async () => {
    if (!clarificationId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const conversations = await fetchConversationsByContextId(
        clarificationId,
        ProcuraconContextType.CONVERSATION_CLARIFICATION
      );

      if (conversations.length > 0) {
        setConversation(conversations[0]);
      }
      fetchedForRef.current = clarificationId;
    } catch {
      setError("Failed to load chat. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [clarificationId]);

  const startChat = useCallback(
    async (firstMessage: string) => {
      if (!clarificationId || !firstMessage.trim()) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const newConversation = await createConversation({
          context_id: clarificationId,
          context_type_id: ProcuraconContextType.CONVERSATION_CLARIFICATION,
          additional_context_id: clarificationId,
          subject: `Clarification Discussion`,
          body: firstMessage,
        });

        if (newConversation) {
          setConversation(newConversation);
          fetchedForRef.current = clarificationId;
        }
      } catch {
        setError("Failed to start chat. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [clarificationId]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (fetchedForRef.current === clarificationId) {
      return;
    }

    fetchExistingConversation();
  }, [isOpen, clarificationId, fetchExistingConversation]);

  useEffect(() => {
    if (!isOpen) {
      setConversation(null);
      fetchedForRef.current = null;
    }
  }, [isOpen]);

  const onConversationUpdated = useCallback((updated: Conversation) => {
    setConversation(updated);
  }, []);

  return {
    conversation,
    isLoading,
    error,
    startChat,
    onConversationUpdated,
  };
};
